/**
 * Developer Platform analytics events (Platform-10, SAIV-11356).
 *
 * Names follow the existing Mixpanel convention of the product apps
 * (camelCase, feature-prefixed, e.g. `integrationApiGenerateToken`).
 * `trackDeveloperPlatformEvent` is the single entry point; pages and
 * actions never call Mixpanel directly.
 */
export const DeveloperPlatformEvent = {
  switcherEntryClick: 'developerPlatformSwitcherEntryClick',
  pageView: 'developerPlatformPageView',
  legacyRedirect: 'developerPlatformLegacyRedirect',
  keyCreate: 'developerPlatformKeyCreate',
  keyCopySecret: 'developerPlatformKeyCopySecret',
  keyRotate: 'developerPlatformKeyRotate',
  keyDisable: 'developerPlatformKeyDisable',
  keyEnable: 'developerPlatformKeyEnable',
  keyDelete: 'developerPlatformKeyDelete',
  keyEdit: 'developerPlatformKeyEdit'
} as const

export type DeveloperPlatformEventName =
  (typeof DeveloperPlatformEvent)[keyof typeof DeveloperPlatformEvent]

export interface DeveloperPlatformEventProps {
  /** Organisation the event happened in. Filled in by the sink when omitted. */
  org_id?: string
  /** Role of the acting user in that organisation. Filled in by the sink when omitted. */
  user_role?: string | null
  /** Product the user came from (`upt`, `compass`, ...) when applicable. */
  source_product?: string
  /** Workspace page id for page views and page-scoped actions. */
  page?: string
  key_id?: string
  environment?: string
  [key: string]: unknown
}

export type DeveloperPlatformAnalyticsSink = (
  name: DeveloperPlatformEventName,
  props: DeveloperPlatformEventProps
) => void

let sink: DeveloperPlatformAnalyticsSink | null = null

/** Installed once by the analytics provider; `null` disables tracking (tests, flag off). */
export function setDeveloperPlatformAnalyticsSink(
  next: DeveloperPlatformAnalyticsSink | null
): void {
  sink = next
}

export function trackDeveloperPlatformEvent(
  name: DeveloperPlatformEventName,
  props: DeveloperPlatformEventProps = {}
): void {
  try {
    sink?.(name, props)
  } catch {
    // Analytics must never break the workspace.
  }
}
